import { useState, useEffect } from 'react'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Share2 } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'
import { Button } from '@/ui/components/common/Button'
import { Label } from '@/ui/components/common/Label'
import { Input } from '@/ui/components/forms/Input'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { useGetSocial, useUpdateSocial } from '@/application/services/settings.service'
import { classNames } from '@/ui/utils/main'
import type { SocialSettings } from '@/domains/types'

type SocialFormState = {
  [K in keyof Omit<SocialSettings, 'updatedAt' | 'footerLinks'>]: string
} & { footerLinks: string[] }

interface SocialFieldConfig {
  key: keyof Omit<SocialSettings, 'updatedAt' | 'footerLinks'>
  networkKey: string
  labelKey: string
  placeholder: string
}

const SOCIAL_FIELDS: SocialFieldConfig[] = [
  {
    key: 'xUrl',
    networkKey: 'x',
    labelKey: 'admin.settings_social_x_label',
    placeholder: 'https://x.com/...',
  },
  {
    key: 'blueskyUrl',
    networkKey: 'bluesky',
    labelKey: 'admin.settings_social_bluesky_label',
    placeholder: 'https://bsky.app/profile/...',
  },
  {
    key: 'facebookUrl',
    networkKey: 'facebook',
    labelKey: 'admin.settings_social_facebook_label',
    placeholder: 'https://facebook.com/...',
  },
  {
    key: 'instagramUrl',
    networkKey: 'instagram',
    labelKey: 'admin.settings_social_instagram_label',
    placeholder: 'https://instagram.com/...',
  },
  {
    key: 'tiktokUrl',
    networkKey: 'tiktok',
    labelKey: 'admin.settings_social_tiktok_label',
    placeholder: 'https://tiktok.com/@...',
  },
  {
    key: 'youtubeUrl',
    networkKey: 'youtube',
    labelKey: 'admin.settings_social_youtube_label',
    placeholder: 'https://youtube.com/@...',
  },
  {
    key: 'twitchUrl',
    networkKey: 'twitch',
    labelKey: 'admin.settings_social_twitch_label',
    placeholder: 'https://twitch.tv/...',
  },
  {
    key: 'discordUrl',
    networkKey: 'discord',
    labelKey: 'admin.settings_social_discord_label',
    placeholder: 'https://discord.gg/...',
  },
]

const EMPTY_FORM: SocialFormState = {
  xUrl: '',
  blueskyUrl: '',
  facebookUrl: '',
  instagramUrl: '',
  tiktokUrl: '',
  youtubeUrl: '',
  twitchUrl: '',
  discordUrl: '',
  footerLinks: [],
}

export function SocialSettingsSection() {
  const { t } = useTranslation()
  const { data: social, isLoading } = useGetSocial()
  const { mutateAsync: updateSocial, isPending } = useUpdateSocial()

  const [form, setForm] = useState<SocialFormState>(EMPTY_FORM)

  useEffect(() => {
    if (social) {
      setForm({
        xUrl: social.xUrl ?? '',
        blueskyUrl: social.blueskyUrl ?? '',
        facebookUrl: social.facebookUrl ?? '',
        instagramUrl: social.instagramUrl ?? '',
        tiktokUrl: social.tiktokUrl ?? '',
        youtubeUrl: social.youtubeUrl ?? '',
        twitchUrl: social.twitchUrl ?? '',
        discordUrl: social.discordUrl ?? '',
        footerLinks: social.footerLinks ?? [],
      })
    }
  }, [social])

  function handleChange(fieldKey: keyof Omit<SocialSettings, 'updatedAt' | 'footerLinks'>) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [fieldKey]: event.target.value }))
    }
  }

  function toggleFooterLink(networkKey: string) {
    setForm((prev) => {
      const current = prev.footerLinks
      if (current.includes(networkKey)) {
        return { ...prev, footerLinks: current.filter((linkKey) => linkKey !== networkKey) }
      }
      if (current.length >= 6) return prev
      return { ...prev, footerLinks: [...current, networkKey] }
    })
  }

  async function handleSave() {
    try {
      const validFooterLinks = form.footerLinks.filter((netKey) => {
        const fieldDef = SOCIAL_FIELDS.find((fieldConfig) => fieldConfig.networkKey === netKey)
        return fieldDef ? Boolean(form[fieldDef.key]) : false
      })
      await updateSocial({
        xUrl: form.xUrl || null,
        blueskyUrl: form.blueskyUrl || null,
        facebookUrl: form.facebookUrl || null,
        instagramUrl: form.instagramUrl || null,
        tiktokUrl: form.tiktokUrl || null,
        youtubeUrl: form.youtubeUrl || null,
        twitchUrl: form.twitchUrl || null,
        discordUrl: form.discordUrl || null,
        footerLinks: validFooterLinks,
      })
      toast.success(t('admin.settings_social_saved'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-4">
          {SOCIAL_FIELDS.map((field) => (
            <Skeleton key={field.key} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Share2 className="w-5 h-5" />
          {t('admin.settings_social_title')}
        </CardTitle>
        <CardDescription>{t('admin.settings_social_card_desc')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {SOCIAL_FIELDS.map((field) => (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={`social-${field.key}`}>{t(field.labelKey)}</Label>
            <Input
              id={`social-${field.key}`}
              type="url"
              value={form[field.key]}
              onChange={handleChange(field.key)}
              placeholder={field.placeholder}
            />
          </div>
        ))}

        {/* Footer links */}
        <div className="space-y-3 pt-2 border-t border-border">
          <div>
            <p className="text-sm font-medium">{t('admin.settings_social_footer_links_title')}</p>
            <p className="text-xs text-muted-foreground">
              {t('admin.settings_social_footer_links_desc')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{form.footerLinks.length} / 6</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {SOCIAL_FIELDS.map((field) => {
              const isChecked = form.footerLinks.includes(field.networkKey)
              const hasUrl = Boolean(form[field.key])
              const isDisabled = !hasUrl || (!isChecked && form.footerLinks.length >= 6)
              return (
                <label
                  key={field.networkKey}
                  className={classNames(
                    'flex items-center gap-2 cursor-pointer text-sm',
                    isDisabled && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => toggleFooterLink(field.networkKey)}
                    className="h-4 w-4 rounded border-border"
                  />
                  {t(field.labelKey)}
                </label>
              )
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => void handleSave()} isLoading={isPending}>
            {t('admin.settings_social_save_button')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
